import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Handles the return leg of Emergent Google OAuth. The app detects
// `#session_id=...` in the URL and renders this component instead of the
// normal routes. We exchange the session for an LCH JWT, then navigate back to
// the same page (hash stripped) — no full reload.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function GoogleAuthCallback() {
  const { loginWithGoogleSession } = useCustomerAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const match = (location.hash || "").match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const dest = location.pathname + location.search; // same page, without hash

    if (!sessionId) {
      navigate(dest, { replace: true });
      return;
    }

    loginWithGoogleSession(sessionId)
      .then(() => toast.success("Signed in with Google"))
      .catch((e) => toast.error(e?.response?.data?.detail || "Google sign-in could not be completed"))
      .finally(() => navigate(dest, { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]" data-testid="google-auth-callback">
      <div className="text-center">
        <Loader2 size={36} className="mx-auto mb-4 text-[#c9a86a] animate-spin" />
        <p className="text-[var(--text-2)] tracking-wide">Completing secure sign-in…</p>
      </div>
    </div>
  );
}
